<template>
    <component
        :is="embedded ? 'div' : 'v-container'"
        :class="embedded ? 'pa-0' : 'pa-4'"
        :style="embedded ? '' : 'max-width: 480px;'">
        <v-card :class="embedded ? 'pa-4 mb-3' : 'pa-4'" :elevation="embedded ? 2 : 4">
            <v-card-title class="text-h6 font-weight-bold">
                <v-icon left color="primary">mdi-fingerprint</v-icon>
                Timbratura
            </v-card-title>
            <v-card-subtitle>
                <div class="text-body-2">{{ currentTime }}</div>
                <div v-if="employeeName" class="text-caption">{{ employeeName }}</div>
                <div v-if="geoStatus" class="text-caption mt-1">
                    <v-icon small :color="geoColor">{{ geoIcon }}</v-icon>
                    {{ geoStatus }}
                </div>
            </v-card-subtitle>

            <v-card-text>
                <div v-if="loadingState" class="text-center py-3">
                    <v-progress-circular indeterminate color="primary" size="28"></v-progress-circular>
                </div>
                <template v-else>
                    <v-alert v-if="state === 'work'" type="success" dense text class="mb-3">
                        <v-icon left small>mdi-briefcase</v-icon>
                        <strong>Sei al lavoro</strong>
                        <span v-if="totals.work">— {{ formatMin(totals.work) }} oggi</span>
                    </v-alert>
                    <v-alert v-else-if="state === 'travel'" type="info" dense text class="mb-3">
                        <v-icon left small>mdi-truck-fast</v-icon>
                        <strong>Sei in viaggio</strong>
                        <span v-if="totals.travel">— {{ formatMin(totals.travel) }} oggi</span>
                    </v-alert>
                    <v-alert v-else type="warning" dense text class="mb-3">
                        <v-icon left small>mdi-pause-circle</v-icon>
                        Giornata non aperta
                    </v-alert>
                </template>

                <div v-if="totals.work || totals.travel" class="text-caption grey--text mb-3">
                    Lavoro {{ formatMin(totals.work) }} · Viaggio {{ formatMin(totals.travel) }} · Totale {{ formatMin(totals.work + totals.travel) }}
                </div>

                <v-text-field
                    v-if="!selfService"
                    v-model="deviceCodeInput"
                    label="Codice NFC / QR (opzionale)"
                    dense
                    outlined
                    prepend-icon="mdi-qrcode"
                    hide-details
                    class="mb-3"
                ></v-text-field>

                <v-alert v-if="message" :type="messageType" dense class="mb-0 mt-2">
                    {{ message }}
                </v-alert>
            </v-card-text>

            <div v-if="!loadingState" class="hr-clock-actions px-4 pb-4">
                <!-- Stato chiuso: bottoni "Inizio viaggio" e "Inizio lavoro" -->
                <template v-if="state === 'closed'">
                    <v-btn color="info" large :loading="submitting === 'travel'" :disabled="!!submitting" @click="transition('travel')">
                        <v-icon left>mdi-truck-fast</v-icon>
                        Inizio viaggio
                    </v-btn>
                    <v-btn color="success" large :loading="submitting === 'work'" :disabled="!!submitting" @click="transition('work')">
                        <v-icon left>mdi-briefcase</v-icon>
                        Inizio lavoro
                    </v-btn>
                </template>

                <!-- Stato in viaggio: switch a lavoro / fine giornata -->
                <template v-else-if="state === 'travel'">
                    <v-btn color="success" large :loading="submitting === 'work'" :disabled="!!submitting" @click="transition('work')">
                        <v-icon left>mdi-briefcase</v-icon>
                        Fine viaggio · Inizio lavoro
                    </v-btn>
                    <v-btn outlined color="error" large :loading="submitting === 'end'" :disabled="!!submitting" @click="transition('end')">
                        <v-icon left>mdi-stop-circle</v-icon>
                        Fine giornata
                    </v-btn>
                </template>

                <!-- Stato al lavoro: switch a viaggio / fine giornata -->
                <template v-else-if="state === 'work'">
                    <v-btn color="info" large :loading="submitting === 'travel'" :disabled="!!submitting" @click="transition('travel')">
                        <v-icon left>mdi-truck-fast</v-icon>
                        Fine lavoro · Inizio viaggio
                    </v-btn>
                    <v-btn color="error" large :loading="submitting === 'end'" :disabled="!!submitting" @click="transition('end')">
                        <v-icon left>mdi-stop-circle</v-icon>
                        Fine giornata
                    </v-btn>
                </template>
            </div>

            <v-card-actions class="pt-0 pb-4 px-4">
                <v-btn small text color="primary" @click="requestLocation">
                    <v-icon left small>mdi-crosshairs-gps</v-icon>
                    Aggiorna posizione
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn small text @click="loadState">
                    <v-icon left small>mdi-refresh</v-icon>
                    Aggiorna
                </v-btn>
            </v-card-actions>
        </v-card>
    </component>
</template>

<script>
export default {
    name: 'hr-clock-widget',

    props: {
        deviceCode: { type: String, default: '' },
        selfService: { type: Boolean, default: false },
        embedded: { type: Boolean, default: false },
    },

    data() {
        return {
            employeeName: null,
            deviceCodeInput: this.deviceCode || '',
            message: null,
            messageType: 'info',
            submitting: null,
            latitude: null,
            longitude: null,
            accuracy: null,
            geoError: null,
            now: new Date(),
            tickTimer: null,
            loadingState: true,
            state: 'closed', // 'closed' | 'work' | 'travel'
            totals: { work: 0, travel: 0 },
        }
    },

    computed: {
        currentTime() {
            return this.now.toLocaleString('it-IT')
        },
        geoStatus() {
            if (this.geoError) return this.geoError
            if (this.latitude && this.longitude) {
                return `Posizione acquisita (±${this.accuracy || '?'}m)`
            }
            return 'Posizione non ancora acquisita'
        },
        geoColor() {
            if (this.geoError) return 'error'
            return this.latitude ? 'success' : 'grey'
        },
        geoIcon() {
            if (this.geoError) return 'mdi-map-marker-off'
            return this.latitude ? 'mdi-map-marker-check' : 'mdi-map-marker-outline'
        },
        stateUrl() {
            return this.selfService ? 'hrme/clock/current-state' : 'hrclocks/current-state'
        },
        transitionUrl() {
            return this.selfService ? 'hrme/clock' : 'hrclocks/transition'
        },
    },

    methods: {
        formatMin(m) {
            if (!m) return '0h 00m'
            const h = Math.floor(m / 60)
            const mm = m % 60
            return `${h}h ${String(mm).padStart(2, '0')}m`
        },
        requestLocation() {
            this.geoError = null
            if (!navigator.geolocation) {
                this.geoError = 'Geolocalizzazione non supportata'
                return
            }
            navigator.geolocation.getCurrentPosition(
                pos => {
                    this.latitude = pos.coords.latitude
                    this.longitude = pos.coords.longitude
                    this.accuracy = Math.round(pos.coords.accuracy)
                },
                err => {
                    this.geoError = err.message || 'Impossibile acquisire la posizione'
                },
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
            )
        },
        loadState() {
            this.axios.get(this.stateUrl).then(r => {
                this.state = r.data.state
                this.totals = r.data.totals_minutes || { work: 0, travel: 0 }
                this.loadingState = false
            }, () => {
                // Su fallimento non mostriamo lo stato 'closed' di default come se
                // fosse reale: diamo un feedback d'errore esplicito.
                this.loadingState = false
                this.message = 'Impossibile recuperare lo stato della timbratura. Riprova.'
                this.messageType = 'error'
            })
        },
        transition(to) {
            this.submitting = to
            this.message = null

            const payload = {
                to,
                source: 'web',
                latitude: this.latitude,
                longitude: this.longitude,
                accuracy_m: this.accuracy,
                clock_device_code: this.deviceCodeInput || null,
            }

            this.axios.post(this.transitionUrl, payload).then(
                r => {
                    this.submitting = null
                    if (r.data.status === 'noop') {
                        this.message = r.data.message
                        this.messageType = 'info'
                    } else {
                        const labels = { travel: 'Viaggio', work: 'Lavoro', end: 'Giornata chiusa' }
                        this.message = labels[to] + ' registrata.'
                        this.messageType = 'success'
                    }
                    this.loadState()
                },
                err => {
                    this.submitting = null
                    this.message = err.response && err.response.data && err.response.data.message
                        ? err.response.data.message
                        : 'Errore durante la timbratura'
                    this.messageType = 'error'
                }
            )
        },
    },

    created() {
        this.$store.commit('endProgress')
        this.tickTimer = setInterval(() => { this.now = new Date() }, 1000)
        this.requestLocation()
        this.loadState()
        const profile = this.$store.getters.getProfile
        this.employeeName = profile ? profile.name : null
    },

    beforeDestroy() {
        if (this.tickTimer) clearInterval(this.tickTimer)
    },
}
</script>

<style scoped>
/* Pulsanti di transizione impilati uno sotto l'altro, a piena larghezza,
   con spaziatura uniforme (sostituisce il margin del v-card-actions che
   disallineava il secondo pulsante). */
.hr-clock-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.hr-clock-actions > .v-btn {
    width: 100%;
}
</style>
